/**
 * CONDITION-FOCUSED HOME PAGE
 * For Shoulder, Elbow, Hand & Wrist specialty sites
 * Focus on CONDITIONS - create curiosity about the doctor
 * Drive traffic to main site (drdnreddyortho.com)
 */

import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { CTASection } from '../components/CTASection';
import { Testimonials } from '../components/Testimonials';
import { useSite } from '../contexts/SiteContext';
import { 
  Activity, 
  ArrowRight,
  CheckCircle2,
  ExternalLink,
  Phone,
  Calendar,
  Star,
  Clock,
  Play,
  Stethoscope,
  ShieldCheck,
  Target,
  Search
} from 'lucide-react';

export const DynamicHome = () => {
  const { 
    heroTitle, 
    heroSubtitle, 
    heroDescription, 
    conditions, 
    symptoms, 
    bodyPart,
    theme,
    doctor,
    specialtyLabel,
    getRelatedSites
  } = useSite();

  // Safe fallbacks
  const safeBodyPart = bodyPart || 'Upper Limb';
  const safeSpecialtyLabel = specialtyLabel || 'Orthopedic Specialist';
  const safeConditions = conditions || [];
  const safeSymptoms = symptoms || [];

  const relatedSites = getRelatedSites ? getRelatedSites() : [];

  // Find the main site for doctor profile link
  const mainSiteUrl = 'https://drdnreddyortho.com';

  const features = [
    {
      title: 'Understand Your Condition',
      description: `Comprehensive information about ${safeBodyPart.toLowerCase()} problems, causes, and symptoms`,
      icon: Search
    },
    {
      title: 'Treatment Options',
      description: 'From conservative care to advanced surgical techniques',
      icon: Stethoscope
    },
    {
      title: 'Recovery Guidance',
      description: 'What to expect during treatment and rehabilitation',
      icon: Target
    }
  ];

  return (
    <div className="min-h-screen" data-testid="dynamic-home">
      {/* Hero Section - Condition Focused */}
      <section className="relative min-h-[80vh] flex items-center overflow-hidden">
        {/* Background */}
        <div 
          className="absolute inset-0"
          style={{ 
            background: `linear-gradient(135deg, ${theme.primaryHex} 0%, ${theme.primaryHex}dd 100%)`
          }}
        />
        
        {/* Pattern overlay */}
        <div className="absolute inset-0 opacity-10">
          <div 
            className="w-full h-full"
            style={{
              backgroundImage: `radial-gradient(circle at 20% 50%, white 1px, transparent 1px)`,
              backgroundSize: '40px 40px'
            }}
          />
        </div>
        
        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6 leading-tight">
              {heroTitle}
              <span className="block mt-2 text-white/90">
                {heroSubtitle}
              </span>
            </h1>
            
            <p className="text-xl md:text-2xl text-white/80 mb-10 leading-relaxed max-w-3xl mx-auto">
              {heroDescription}
            </p>

            {/* CTA Buttons */}
            <div className="flex flex-col sm:flex-row gap-4 justify-center mb-12">
              <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
                <Button 
                  size="lg" 
                  className="bg-white hover:bg-gray-100 text-lg px-8 py-6"
                  style={{ color: theme.primaryHex }}
                  data-testid="hero-book-btn"
                >
                  <Calendar className="w-5 h-5 mr-2" />
                  Book Expert Consultation
                </Button>
              </a>
              <a href={`tel:${doctor.phone}`}>
                <Button 
                  size="lg" 
                  variant="outline" 
                  className="border-2 border-white text-white hover:bg-white/10 text-lg px-8 py-6"
                  data-testid="hero-call-btn"
                >
                  <Phone className="w-5 h-5 mr-2" />
                  {doctor.phone}
                </Button>
              </a>
            </div>

            {/* Quick Stats */}
            <div className="flex flex-wrap justify-center gap-8 text-white/90">
              <div className="flex items-center gap-2">
                <Star className="w-5 h-5 fill-yellow-400 text-yellow-400" />
                <span>5.0 Google Rating</span>
              </div>
              <div className="flex items-center gap-2">
                <Clock className="w-5 h-5" />
                <span>30+ Years Experience</span>
              </div>
              <div className="flex items-center gap-2">
                <ShieldCheck className="w-5 h-5" />
                <span>UK & USA Fellowship</span>
              </div>
            </div>
          </div>
        </div>

        {/* Scroll indicator */}
        <div className="absolute bottom-8 left-1/2 -translate-x-1/2 text-white/60 animate-bounce">
          <div className="flex flex-col items-center gap-2">
            <span className="text-sm">Explore {safeBodyPart} Conditions</span>
            <ArrowRight className="w-5 h-5 rotate-90" />
          </div>
        </div>
      </section>

      {/* What You'll Find Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Your Complete {safeBodyPart} Resource
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Everything you need to know about {safeBodyPart.toLowerCase()} conditions, treatments, and recovery
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {features.map((feature, index) => {
              const Icon = feature.icon;
              return (
                <div 
                  key={index} 
                  className="text-center p-8 rounded-2xl"
                  style={{ backgroundColor: `${theme.primaryHex}08` }}
                >
                  <div 
                    className="w-16 h-16 rounded-2xl flex items-center justify-center mx-auto mb-6"
                    style={{ backgroundColor: `${theme.primaryHex}15` }}
                  >
                    <Icon className="w-8 h-8" style={{ color: theme.primaryHex }} />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{feature.title}</h3>
                  <p className="text-gray-600">{feature.description}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Conditions Section - Main Focus */}
      <section className="py-20" style={{ backgroundColor: theme?.lightBgHex || '#f0f9ff' }}>
        <div className="container mx-auto px-4">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
              Common {safeBodyPart} Conditions
            </h2>
            <p className="text-lg text-gray-600 max-w-2xl mx-auto">
              Click on any condition to learn about causes, symptoms, and treatment options
            </p>
          </div>
          
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {safeConditions.slice(0, 9).map((condition, index) => (
              <Link key={condition.id} to={condition.slug}>
                <Card 
                  className="h-full hover:shadow-xl transition-all cursor-pointer group border-0 shadow-md hover:-translate-y-1"
                  data-testid={`condition-card-${condition.id}`}
                >
                  <CardHeader className="pb-2">
                    <div 
                      className="w-12 h-12 rounded-xl flex items-center justify-center mb-4 group-hover:scale-110 transition-transform"
                      style={{ backgroundColor: `${theme.primaryHex}15` }}
                    >
                      <Activity className="w-6 h-6" style={{ color: theme.primaryHex }} />
                    </div>
                    <CardTitle className="text-xl text-gray-900 group-hover:opacity-80">
                      {condition.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-gray-600 mb-4 line-clamp-2">
                      {condition.description}
                    </CardDescription>
                    <span 
                      className="font-medium inline-flex items-center gap-2 group-hover:gap-3 transition-all"
                      style={{ color: theme.primaryHex }}
                    >
                      Learn More
                      <ArrowRight className="w-4 h-4" />
                    </span>
                  </CardContent>
                </Card>
              </Link>
            ))}
          </div>

          {conditions.length > 9 && (
            <div className="text-center mt-10">
              <Button 
                variant="outline" 
                size="lg"
                style={{ borderColor: theme.primaryHex, color: theme.primaryHex }}
              >
                View All {conditions.length} Conditions
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* When to Seek Help */}
      <section className="py-20 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-6">
                When to Seek Expert Help?
              </h2>
              <p className="text-lg text-gray-600 mb-8">
                Don't ignore these warning signs. Early treatment leads to better outcomes.
              </p>
              <div className="space-y-4">
                {safeSymptoms.slice(0, 6).map((symptom, index) => (
                  <div 
                    key={index} 
                    className="flex items-start gap-3 p-4 rounded-xl bg-gray-50"
                  >
                    <CheckCircle2 
                      className="w-6 h-6 flex-shrink-0 mt-0.5" 
                      style={{ color: theme.primaryHex }}
                    />
                    <p className="text-gray-700">{symptom}</p>
                  </div>
                ))}
              </div>
            </div>
            
            {/* Video CTA */}
            <div className="relative">
              <div 
                className="aspect-video rounded-2xl overflow-hidden shadow-xl"
                style={{ backgroundColor: theme.primaryHex }}
              >
                <img
                  src="https://images.unsplash.com/photo-1551076805-e1869033e561?w=800"
                  alt={`${doctor?.name || 'Dr. DN Reddy'} orthopedic consultation at Apollo Hospitals, Financial District, Hyderabad`}
                  className="w-full h-full object-cover opacity-80"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <a 
                    href={doctor.socialMedia?.youtube || 'https://www.youtube.com/@DrDNReddy'}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-20 h-20 rounded-full bg-white flex items-center justify-center hover:scale-110 transition-transform shadow-2xl"
                  >
                    <Play className="w-8 h-8 ml-1" style={{ color: theme.primaryHex }} />
                  </a>
                </div>
                <div className="absolute bottom-4 left-4 right-4 text-white">
                  <p className="font-semibold">Watch Patient Success Stories</p>
                  <p className="text-sm text-white/80">Real recovery journeys on YouTube</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Meet the Specialist - Teaser to Main Site */}
      <section className="py-20" style={{ backgroundColor: theme.primaryHex }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              Treated by a World-Class {specialtyLabel}
            </h2>
            <p className="text-xl text-white/80 mb-4">
              Fellowship trained in UK, Mayo Clinic & Florida • 30+ Years Experience
            </p>
            <p className="text-lg text-white/70 mb-8">
              5000+ successful surgeries • 5.0 Google Rating
            </p>
            
            <a href={mainSiteUrl} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                className="bg-white hover:bg-gray-100 text-lg px-8"
                style={{ color: theme.primaryHex }}
              >
                Meet the Specialist
                <ExternalLink className="w-5 h-5 ml-2" />
              </Button>
            </a>
            
            <p className="text-sm text-white/60 mt-4">
              Visit drdnreddyortho.com to learn about your treating surgeon
            </p>
          </div>
        </div>
      </section>

      {/* Related Specialties */}
      {relatedSites.length > 0 && (
        <section className="py-20 bg-white">
          <div className="container mx-auto px-4">
            <div className="text-center mb-12">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4">
                Complete Upper Limb Care
              </h2>
              <p className="text-lg text-gray-600 max-w-2xl mx-auto">
                Comprehensive treatment for all upper limb conditions
              </p>
            </div>
            <div className="grid md:grid-cols-3 gap-6 max-w-4xl mx-auto">
              {relatedSites.map((site) => (
                <a
                  key={site.id}
                  href={`https://${site.domain}`}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block group"
                >
                  <Card 
                    className="h-full hover:shadow-xl transition-all cursor-pointer text-center border-t-4"
                    style={{ borderTopColor: site.theme.primaryHex }}
                  >
                    <CardHeader>
                      <div 
                        className="w-16 h-16 rounded-full flex items-center justify-center mx-auto mb-4 group-hover:scale-110 transition-transform"
                        style={{ backgroundColor: `${site.theme.primaryHex}20` }}
                      >
                        <span className="text-3xl">{site.icon}</span>
                      </div>
                      <CardTitle className="text-xl">{site.bodyPart || site.title}</CardTitle>
                      <CardDescription>{site.tagline}</CardDescription>
                    </CardHeader>
                    <CardContent>
                      <span 
                        className="font-medium inline-flex items-center gap-2"
                        style={{ color: site.theme.primaryHex }}
                      >
                        Explore
                        <ExternalLink className="w-4 h-4" />
                      </span>
                    </CardContent>
                  </Card>
                </a>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Testimonials */}
      <Testimonials />

      {/* Final CTA */}
      <section className="py-16 bg-gray-900 text-white">
        <div className="container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-bold mb-4">
            Ready to Get Expert Help?
          </h2>
          <p className="text-xl text-gray-300 mb-8 max-w-2xl mx-auto">
            Book a consultation with a specialist who has treated 15,000+ patients
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href={doctor.consultationLink} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                style={{ backgroundColor: theme.primaryHex }}
                className="text-white"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Book Appointment
              </Button>
            </a>
            <a href={doctor.whatsappLink} target="_blank" rel="noopener noreferrer">
              <Button 
                size="lg" 
                variant="outline"
                className="border-white text-white hover:bg-white/10"
              >
                💬 WhatsApp Us
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};
